/**
 * STAIR STUDIO stage 2 (2026-10-09) — the structure catalog (docs/bim-plan/30-stair-studio.md
 * "Structure"). A stair's STRUCTURE is how it is built: the members that carry it, the treads
 * and risers they carry, its landings. Each entry is a firm-curated character with real
 * starting sizes (every structural option is an engineered item; these are drawing sizes, not
 * calcs). The look lives on each stair (Matt's decision 1): the stair's own `structure` instance
 * param picks the entry, and its own overrides (member size, tread thickness, risers, wall side,
 * materials) fall back to the entry's defaults when blank.
 *
 * 'solid' is today's stair — the monolithic body + its tread finish — and the default, so every
 * existing stair looks exactly the same (decision 2).
 */
import { counterEdgeTiers } from './caseworkCatalog'

export type StairMemberKind = 'plate' | 'channel' | 'hss' | 'wide' | 'lumber'

/** a member section, inches: depth d (vertical), width b (across), web / plate t, flange tf */
export interface StairMember {
  value: string
  label: string
  kind: StairMemberKind
  d: number
  b: number
  t: number
  tf?: number
}

export const STAIR_MEMBERS: StairMember[] = [
  { value: '2x12', label: '2×12', kind: 'lumber', d: 11.25, b: 1.5, t: 1.5 },
  { value: 'lvl12', label: 'LVL 1-3/4 × 11-7/8', kind: 'lumber', d: 11.875, b: 1.75, t: 1.75 },
  { value: '5/4x12', label: '5/4 × 12 board', kind: 'lumber', d: 11.25, b: 1.0, t: 1.0 },
  { value: 'pl12', label: 'Plate 1/2 × 12', kind: 'plate', d: 12, b: 0.5, t: 0.5 },
  { value: 'pl14', label: 'Plate 3/4 × 14', kind: 'plate', d: 14, b: 0.75, t: 0.75 },
  { value: 'pl3z', label: 'Plate 3/8 (zig-zag)', kind: 'plate', d: 4, b: 0.375, t: 0.375 },
  { value: 'c10', label: 'C10 channel', kind: 'channel', d: 10, b: 2.6, t: 0.24, tf: 0.44 },
  { value: 'c12', label: 'C12 channel', kind: 'channel', d: 12, b: 3.0, t: 0.28, tf: 0.5 },
  { value: 'hss3x10', label: 'HSS 10 × 3', kind: 'hss', d: 10, b: 3, t: 0.25 },
  { value: 'hss4x12', label: 'HSS 12 × 4', kind: 'hss', d: 12, b: 4, t: 0.3125 },
  { value: 'hss4x8', label: 'HSS 8 × 4', kind: 'hss', d: 8, b: 4, t: 0.25 },
  { value: 'hss6x6', label: 'HSS 6 × 6', kind: 'hss', d: 6, b: 6, t: 0.3125 },
  { value: 'hss8x8', label: 'HSS 8 × 8', kind: 'hss', d: 8, b: 8, t: 0.375 },
  { value: 'hss12x8', label: 'HSS 12 × 8', kind: 'hss', d: 12, b: 8, t: 0.375 },
  { value: 'w8', label: 'W8 beam', kind: 'wide', d: 8, b: 5.25, t: 0.23, tf: 0.36 },
  { value: 'w10', label: 'W10 beam', kind: 'wide', d: 10, b: 5.75, t: 0.25, tf: 0.43 },
]

export type StairMaterialRole = 'wood' | 'paint' | 'steel' | 'concrete' | 'stone'

export interface StairStructure {
  value: string
  label: string
  group: 'Solid' | 'Wood' | 'Steel' | 'Floating' | 'Plate and rod' | 'Masonry'
  note: string
  /** the member sizes it offers (first = default); [] = no members */
  members: string[]
  /** tread thickness, inches (0 = no separate tread — the structure is the walking surface) */
  treadIn: number
  /** closed (riser boards) or open by default */
  risers: 'closed' | 'open'
  /** anchored into a wall on one side (the wall side matters) */
  wall?: boolean
  /** default looks of its parts */
  structureRole: StairMaterialRole
  treadRole: StairMaterialRole
  riserRole: StairMaterialRole
}

export const STAIR_STRUCTURES: StairStructure[] = [
  { value: 'solid', label: 'Solid / plastered', group: 'Solid', note: 'One solid mass with a sloped soffit, finished with tread boards — how every stair has been built so far', members: [], treadIn: 0, risers: 'closed', structureRole: 'paint', treadRole: 'wood', riserRole: 'paint' },
  { value: 'woodCarriage', label: 'Wood carriage', group: 'Wood', note: 'Three notched carriage stringers under 1" treads and painted risers — the framed stair', members: ['2x12', 'lvl12'], treadIn: 1, risers: 'closed', structureRole: 'wood', treadRole: 'wood', riserRole: 'paint' },
  { value: 'housed', label: 'Housed stringers', group: 'Wood', note: 'Closed stringers either side with the treads and risers let into them — the traditional finish stair', members: ['5/4x12', '2x12'], treadIn: 1.25, risers: 'closed', structureRole: 'paint', treadRole: 'wood', riserRole: 'paint' },
  { value: 'openStringer', label: 'Open cut stringer', group: 'Wood', note: 'Cut-and-mitered stringers that show the step profile, treads overhanging the ends', members: ['2x12', 'lvl12'], treadIn: 1.25, risers: 'closed', structureRole: 'paint', treadRole: 'wood', riserRole: 'paint' },
  { value: 'steelPlate', label: 'Steel plate stringers', group: 'Steel', note: 'Flat plates either side, treads between them on clips, open risers', members: ['pl12', 'pl14'], treadIn: 2, risers: 'open', structureRole: 'steel', treadRole: 'wood', riserRole: 'steel' },
  { value: 'channel', label: 'Steel C-channel', group: 'Steel', note: 'C-channels either side, toes out, treads between — industrial / loft', members: ['c12', 'c10'], treadIn: 2, risers: 'open', structureRole: 'steel', treadRole: 'wood', riserRole: 'steel' },
  { value: 'hssDouble', label: 'HSS tube stringers', group: 'Steel', note: 'Two rectangular tubes under the tread ends — a clean box profile', members: ['hss3x10', 'hss4x12'], treadIn: 2, risers: 'open', structureRole: 'steel', treadRole: 'wood', riserRole: 'steel' },
  { value: 'zigzag', label: 'Zig-zag plate', group: 'Steel', note: 'Plates cut to the step profile under the tread ends', members: ['pl3z'], treadIn: 2, risers: 'open', structureRole: 'steel', treadRole: 'wood', riserRole: 'steel' },
  { value: 'monoHss', label: 'Mono-stringer, tube', group: 'Steel', note: 'One center tube spine with an arm welded under each tread', members: ['hss8x8', 'hss6x6', 'hss12x8'], treadIn: 2.5, risers: 'open', structureRole: 'steel', treadRole: 'wood', riserRole: 'steel' },
  { value: 'monoBeam', label: 'Mono-stringer, I-beam', group: 'Steel', note: 'One center wide-flange spine with an arm under each tread', members: ['w8', 'w10'], treadIn: 2.5, risers: 'open', structureRole: 'steel', treadRole: 'wood', riserRole: 'steel' },
  { value: 'doubleMono', label: 'Double mono-stringer', group: 'Steel', note: 'Two tube spines set in from the ends, arms across', members: ['hss4x8', 'hss6x6'], treadIn: 2.5, risers: 'open', structureRole: 'steel', treadRole: 'wood', riserRole: 'steel' },
  { value: 'hiddenWall', label: 'Floating, hidden wall stringer', group: 'Floating', note: 'A steel stringer buried in the wall; solid slab treads cantilever off it', members: ['pl12', 'hss4x12'], treadIn: 3, risers: 'open', wall: true, structureRole: 'steel', treadRole: 'wood', riserRole: 'wood' },
  { value: 'sleeve', label: 'Floating, wrapped steel core', group: 'Floating', note: 'A hidden steel tube cantilevers from a spine in the wall; the wood tread slides over it like a sleeve', members: ['pl14', 'hss4x12'], treadIn: 3.5, risers: 'open', wall: true, structureRole: 'steel', treadRole: 'wood', riserRole: 'wood' },
  { value: 'knifePlate', label: 'Floating, knife plates', group: 'Floating', note: 'Steel plates out of the wall, let into a kerf in each solid tread', members: ['pl12'], treadIn: 3, risers: 'open', wall: true, structureRole: 'steel', treadRole: 'wood', riserRole: 'wood' },
  { value: 'ribbon', label: 'Folded steel ribbon', group: 'Plate and rod', note: 'One brake-formed plate, tread and riser in one, thin as paper', members: [], treadIn: 0, risers: 'closed', structureRole: 'steel', treadRole: 'wood', riserRole: 'steel' },
  { value: 'suspended', label: 'Hung on rods', group: 'Plate and rod', note: 'Treads anchored in the wall and hung from above on steel rods — the rods are the guard', members: ['pl12'], treadIn: 2.5, risers: 'open', wall: true, structureRole: 'steel', treadRole: 'wood', riserRole: 'wood' },
  { value: 'concrete', label: 'Cast concrete', group: 'Masonry', note: 'Cast-in-place concrete, one mass with a sloped soffit', members: [], treadIn: 0, risers: 'closed', structureRole: 'concrete', treadRole: 'concrete', riserRole: 'concrete' },
  { value: 'stoneCantilever', label: 'Stone cantilever', group: 'Masonry', note: 'Solid stone treads built into the wall, each bearing on the one below', members: [], treadIn: 6, risers: 'open', wall: true, structureRole: 'stone', treadRole: 'stone', riserRole: 'stone' },
  { value: 'timberBlock', label: 'Timber blocks', group: 'Masonry', note: 'Solid timber blocks a full step tall, cantilevered from the wall', members: [], treadIn: 0, risers: 'open', wall: true, structureRole: 'wood', treadRole: 'wood', riserRole: 'wood' },
]

/** the default look of each material role (a tinted built-in or a bare metal) */
export const STAIR_ROLE_DEFAULTS: Record<StairMaterialRole, { hex: string; metal?: string }> = {
  wood: { hex: '#b48a5c' },         // rift white oak
  paint: { hex: '#f1eee7' },        // paint-grade white
  steel: { hex: '#2e2d2b', metal: 'blackened' },
  concrete: { hex: '#b9b4ab' },
  stone: { hex: '#d9d1c3' },        // limestone
}

export function stairStructureOf(v: unknown): StairStructure {
  return STAIR_STRUCTURES.find((s) => s.value === v) ?? STAIR_STRUCTURES[0]
}

export function stairMemberOf(structure: StairStructure, v: unknown): StairMember | null {
  const key = structure.members.includes(String(v)) ? String(v) : structure.members[0]
  return STAIR_MEMBERS.find((m) => m.value === key) ?? null
}

/**
 * The effective construction of a stair: its own instance choices over its structure's
 * defaults. `params` = the stair's resolved params.
 */
export interface StairConstruction {
  structure: StairStructure
  member: StairMember | null
  /** feet */
  treadT: number
  openRisers: boolean
  /** +1 = the wall is on the walker's left, −1 = right */
  wallSign: 1 | -1
}

export function stairConstructionOf(params: Record<string, unknown>): StairConstruction {
  const structure = stairStructureOf(params.structure)
  const member = stairMemberOf(structure, params.structureSize)
  const t = Number(params.treadThicknessIn)
  const treadIn = Number.isFinite(t) && t > 0 && params.treadThicknessIn !== null ? t : structure.treadIn
  const rm = params.riserMode
  const openRisers = rm === 'open' ? true : rm === 'closed' ? false : structure.risers === 'open'
  return { structure, member, treadT: treadIn / 12, openRisers, wallSign: params.wallSide === 'right' ? -1 : 1 }
}

// ---------- STAIR STUDIO stage 4: treads, risers, nosings, landings, soffit + skirt details ----------

/** the nosing profiles (the tread's front edge) */
export const STAIR_NOSINGS = [
  { value: 'square', label: 'Square' },
  { value: 'eased', label: 'Eased' },
  { value: 'bullnose', label: 'Bullnose' },
  { value: 'halfRound', label: 'Half round' },
  { value: 'cove', label: 'Cove under' },
] as const

/**
 * A nosing profile as stacked tiers (feet, from the tread's underside), each set back `inset`
 * from the tread's front — the countertop edge model (`counterEdgeTiers`), so a profiled nosing
 * reads in 3D, sections and elevations while every tread stays a plain box per tier.
 */
export function stairNosingTiers(profile: string, T: number): { z0: number; z1: number; inset: number }[] {
  if (T <= 1e-6) return []
  if (profile === 'eased') {
    const e = Math.min(1 / 8 / 12, T * 0.3)
    return [{ z0: 0, z1: T - e, inset: 0 }, { z0: T - e, z1: T, inset: e * 0.5 }]
  }
  if (profile === 'bullnose') return counterEdgeTiers('fullBullnose', T)
  if (profile === 'halfRound') return counterEdgeTiers('halfBullnose', T)
  return [{ z0: 0, z1: T, inset: 0 }]
}

export interface StairDetails {
  nosingProfile: string
  /** a recessed riser with a shadow gap under the tread */
  riserReveal: boolean
  /** a steel bar across each open riser (halves the gap for the 4" sphere) */
  riserBar: boolean
  soffit: 'open' | 'closed'
  skirt: 'none' | 'wall' | 'both'
  /** the open side clad in plaster to a thin edge, down to the soffit */
  knifeEdge: boolean
  /** a nosing at each landing's front edge, matching the treads */
  landingNosing: boolean
}

/** structures whose underside can be closed with a soffit and whose sides take a skirt */
export const STAIR_SOFFIT_STRUCTURES = ['woodCarriage', 'housed', 'openStringer', 'steelPlate', 'channel', 'hssDouble', 'zigzag', 'monoHss', 'monoBeam', 'doubleMono']
/** structures whose own side stringers stand where a skirt would */
export const STAIR_SIDE_STRINGER_STRUCTURES = ['housed', 'steelPlate', 'channel']

export function stairDetailsOf(params: Record<string, unknown>): StairDetails {
  const pick = <T extends string>(v: unknown, ok: readonly T[], d: T): T => (ok.includes(v as T) ? (v as T) : d)
  return {
    // a solid stair keeps its square nosing unless one is picked (existing stairs never change)
    nosingProfile: pick(params.nosingProfile, STAIR_NOSINGS.map((n) => n.value), !params.structure || params.structure === 'solid' ? 'square' : 'eased'),
    riserReveal: params.riserReveal === true,
    riserBar: params.riserBar === true,
    soffit: pick(params.soffit, ['open', 'closed'] as const, 'open'),
    skirt: pick(params.skirt, ['none', 'wall', 'both'] as const, 'none'),
    knifeEdge: params.knifeEdge === true,
    landingNosing: params.landingNosing !== false,
  }
}
